import { useState, useCallback, useRef } from "react";

interface SpeedTestResult {
  downloadSpeed: number; // Mbps
  latency: number; // ms
}

interface UseWifiSpeedTestReturn {
  isRunning: boolean;
  progress: number; // 0-100
  result: SpeedTestResult | null;
  error: string | null;
  runSpeedTest: () => Promise<SpeedTestResult | null>;
  reset: () => void;
}

function isDebugEnabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "1";
  } catch {
    return false;
  }
}

function round1(n: number) {
  return Math.round(n * 10) / 10;
}

/**
 * Professional-grade speed test using parallel connections.
 * Similar methodology to Google/Ookla speed tests:
 * - Multiple parallel TCP connections to saturate the link
 * - Continuous sampling over a fixed duration
 * - Discard warm-up period (TCP slow-start)
 * - Use trimmed mean of stable samples
 */
export function useWifiSpeedTest(): UseWifiSpeedTestReturn {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<SpeedTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(0);
  }, []);

  const runSpeedTest = useCallback(async (): Promise<SpeedTestResult | null> => {
    const debug = isDebugEnabled();
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    // Abort any previous test
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsRunning(true);
    setError(null);
    setResult(null);
    setProgress(0);

    try {
      // Phase 0: Warm-up request (establishes TCP, warms mobile radio)
      // Non-fatal: if this fails we keep going — the main test may still work.
      setProgress(3);
      try {
        await fetch("https://speed.cloudflare.com/__down?bytes=100000&cachebust=warmup_" + Date.now(), {
          method: "GET",
          cache: "no-store",
          signal,
        });
      } catch (e) {
        if (signal.aborted) throw e;
        if (debug) console.warn("[SpeedTest] Warmup failed, continuing:", e);
      }

      // Phase 1: Measure latency
      // Non-fatal: fall back to a reasonable default if blocked.
      setProgress(8);
      let latency = 0;
      try {
        const latencyStart = performance.now();
        await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
          method: "GET",
          cache: "no-store",
          signal,
        });
        latency = Math.round(performance.now() - latencyStart);
      } catch (e) {
        if (signal.aborted) throw e;
        if (debug) console.warn("[SpeedTest] Latency probe failed, defaulting to 0:", e);
        latency = 0;
      }

      if (debug) console.info("[SpeedTest] Latency:", latency, "ms | Mobile:", isMobile);

      // Phase 2: Parallel download test
      // Mobile: fewer connections + smaller chunks to avoid browser throttling
      const numConnections = isMobile ? 2 : 4;
      const testDurationMs = isMobile ? 5000 : 6000;
      const chunkSize = isMobile ? 500_000 : 2_000_000;
      const baseUrl = "https://speed.cloudflare.com/__down";

      // Track all bytes downloaded and timing
      let totalBytesDownloaded = 0;
      const samples: { timestamp: number; bytes: number }[] = [];
      const startTime = performance.now();

      // Create parallel download workers
      // Wrap the entire worker body in try/catch so one failing connection
      // doesn't reject Promise.all and kill the whole test.
      const downloadWorker = async (connectionId: number) => {
        try {
          let requestCount = 0;

          while (performance.now() - startTime < testDurationMs) {
            if (signal.aborted) break;

            const url = `${baseUrl}?bytes=${chunkSize}&cachebust=${Date.now()}_${connectionId}_${requestCount++}`;

            try {
              const chunkStart = performance.now();
              const response = await fetch(url, {
                method: "GET",
                cache: "no-store",
                signal,
              });

              if (!response.ok) continue;

              // Stream the response to measure progressive download
              const reader = response.body?.getReader();
              if (!reader) continue;

              let chunkBytes = 0;
              try {
                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  if (signal.aborted) {
                    reader.cancel().catch(() => {});
                    break;
                  }
                  chunkBytes += value.length;
                  totalBytesDownloaded += value.length;

                  // Record sample every ~200ms worth of data
                  samples.push({
                    timestamp: performance.now() - startTime,
                    bytes: value.length,
                  });
                }
              } catch (streamErr) {
                // Mid-stream connection drop — release the reader and try the next chunk.
                if (signal.aborted) break;
                if (debug) console.warn(`[SpeedTest] Connection ${connectionId} stream error:`, streamErr);
                try { reader.cancel(); } catch {}
              }

              if (debug && requestCount <= 2) {
                const chunkDuration = performance.now() - chunkStart;
                console.info(`[SpeedTest] Connection ${connectionId} chunk: ${round1(chunkBytes / 1000)}KB in ${Math.round(chunkDuration)}ms`);
              }
            } catch (e) {
              if (signal.aborted) break;
              // Network hiccup, continue
              if (debug) console.warn(`[SpeedTest] Connection ${connectionId} fetch error:`, e);
            }
          }
        } catch (workerErr) {
          // Last-resort guard so the worker promise always resolves.
          if (debug) console.warn(`[SpeedTest] Connection ${connectionId} fatal error:`, workerErr);
        }
      };

      setProgress(15);

      // Start all parallel downloads
      const workers = Array.from({ length: numConnections }, (_, i) => downloadWorker(i));

      // Update progress periodically
      const progressInterval = setInterval(() => {
        const elapsed = performance.now() - startTime;
        const pct = Math.min(15 + (elapsed / testDurationMs) * 80, 95);
        setProgress(Math.round(pct));
      }, 200);

      // Wait for all workers to complete
      await Promise.all(workers);
      clearInterval(progressInterval);

      setProgress(95);

      const totalDurationMs = performance.now() - startTime;

      if (samples.length < 10) {
        if (totalBytesDownloaded === 0) {
          throw new Error("Couldn't reach the speed test server. An ad blocker, VPN, or firewall may be blocking it — try disabling them or rate manually below.");
        }
        throw new Error("Connection too unstable to measure. Try again or rate manually below.");
      }

      // Calculate speed using trimmed samples
      // Discard first 20% (warm-up) and last 10% (cooldown)
      const warmupCutoff = testDurationMs * 0.2;
      const cooldownCutoff = testDurationMs * 0.9;
      
      const stableSamples = samples.filter(
        s => s.timestamp >= warmupCutoff && s.timestamp <= cooldownCutoff
      );

      if (stableSamples.length < 5) {
        // Fallback: use all samples
        stableSamples.push(...samples);
      }

      // Calculate throughput from stable period
      const stableBytes = stableSamples.reduce((sum, s) => sum + s.bytes, 0);
      const stableDurationMs = (stableSamples[stableSamples.length - 1]?.timestamp || testDurationMs) - 
                               (stableSamples[0]?.timestamp || 0);
      
      // Also calculate using total bytes as sanity check
      const totalMegabits = (totalBytesDownloaded * 8) / 1_000_000;
      const totalSpeedMbps = totalMegabits / (totalDurationMs / 1000);

      const stableMegabits = (stableBytes * 8) / 1_000_000;
      const stableSpeedMbps = stableDurationMs > 0 
        ? stableMegabits / (stableDurationMs / 1000)
        : totalSpeedMbps;

      // Use the higher of stable or total (stable should be higher once warm-up is excluded)
      const speedMbps = Math.max(stableSpeedMbps, totalSpeedMbps * 0.9);

      if (debug) {
        console.info("[SpeedTest] Results:", {
          totalBytes: totalBytesDownloaded,
          totalDurationMs: Math.round(totalDurationMs),
          totalSpeedMbps: round1(totalSpeedMbps),
          stableBytes,
          stableDurationMs: Math.round(stableDurationMs),
          stableSpeedMbps: round1(stableSpeedMbps),
          finalSpeedMbps: round1(speedMbps),
          sampleCount: samples.length,
          stableSampleCount: stableSamples.length,
        });
      }

      const finalResult: SpeedTestResult = {
        downloadSpeed: round1(speedMbps),
        latency,
      };

      setProgress(100);
      setResult(finalResult);
      setIsRunning(false);
      return finalResult;
    } catch (err) {
      if (signal.aborted) {
        setIsRunning(false);
        return null;
      }
      const errorMessage = err instanceof Error ? err.message : "Speed test failed";
      setError(errorMessage);
      setIsRunning(false);
      return null;
    }
  }, []);

  return {
    isRunning,
    progress,
    result,
    error,
    runSpeedTest,
    reset,
  };
}
